import cloneDeep from "lodash.clonedeep";
import isEqualWith from "lodash.isequalwith";
import {
  forwardRef,
  useCallback,
  useContext,
  useEffect,
  useReducer,
  useRef,
  useState,
} from "react";
import { shallowEqual, useDispatch, useSelector } from "react-redux";
import { useNavigate } from "react-router";
import { IconBeach, IconBuildingSkyscraper, IconBus, IconPlane } from "@tabler/icons-react";
import Card from "src/components/UI/Card/Card";
import PaddedView from "src/components/UI/PaddedView/PaddedView";
import { TRIP_TYPE as TRIP } from "src/constants";
import { ModalElementContext } from "src/context/modal-element-context";
import { useLocaleContext } from "src/context/locale-context";
import { searchDataActions } from "src/store/search";

import classes from "./SearchForm.module.css";
import { SearchTypeSwitch } from "./SearchTypeSwitch";
import { DesktopSingleSearchForm } from "./elements/SingleSearchForm/DesktopSingleSearchForm";
import TravelTypeItemGroup from "./elements/TravelTypeItemGroup/TravelTypeItemGroup";
import { getSearchFormElementContentById } from "./search-form-utils";

function dropdownReducer(state, action) {
  if (action.type === "OPEN_DROPDOWN") {
    return {
      targetTripId: action.tripId ? action.tripId : state.targetTripId,
      isElementClosing: false,
      currentElement: action.content,
      dropdownSelector: action.selector,
      focusOnInit: action.focus,
    };
  } else if (action.type === "CLOSE_DROPDOWN") {
    return { ...state, isElementClosing: true, currentElement: null };
  }
}

function cloneEssentialSearchData(searchData) {
  const clonedData = cloneDeep(searchData);
  delete clonedData.userInput;
  clonedData.trips.forEach((t) => {
    delete t.srcLocation.names;
    delete t.destLocation.names;
  });
  return clonedData;
}

function isEqualCustomized(obj1, obj2) {
  const customizer = (value1, value2, key) => {
    if (key === "id") {
      return true; // Skip comparing "id"
    } else if (typeof value1 === "string" && typeof value2 === "string") {
      return value1.toLowerCase() === value2.toLowerCase();
    }
  };

  return isEqualWith(obj1, obj2, customizer);
}

export function DesktopSearchForm({
  isResultsPage,
  onSearchStart,
  hideTripTypes,
  hideCheckboxes,
  searchType,
  heading,
  hideCabinOptions,
}) {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { getStringRes } = useLocaleContext();
  const elementRef = useRef(null);
  const inputRef = useRef({});

  const onSelectSearchType = (type) => {
    if (type === searchType) return;
    if (type === "flights") return navigate("/");
    if (type === "hotels") return navigate("/hotels");
    if (type === "charters") return navigate("/charter-flights");
    if (type === "tours") return navigate("/bus-tours");
  };

  const searchData = useSelector((state) => state.search, shallowEqual);
  const tripType = searchData.tripType;

  const prevSFData = useRef(cloneEssentialSearchData(searchData));
  const [showSearchButton, setShowSearchButton] = useState(!isResultsPage);
  const [checkAccommodation, setCheckAccommodation] = useState(false);
  const [checkDirectFlights, setCheckDirectFlights] = useState(false);

  const [dropdownState, dispatchDropdown] = useReducer(dropdownReducer, {
    targetTripId: searchData.trips[0].id,
    currentElement: null,
    isElementClosing: false,
    dropdownSelector: "",
    focusOnInit: true,
  });

  const onSelectTripTypeHandler = (type) => {
    dispatch(searchDataActions.updateTripType(type));
    if (type !== TRIP.roundtrip) {
      dispatch(
        searchDataActions.updateEndDate({
          tripId: searchData.trips[0].id,
          date: "",
        }),
      );
    }
  };

  useEffect(() => {
    const searchFormHeader = document.getElementById(
      "search-form-section" + (isResultsPage ? "-results" : ""),
    );
    searchFormHeader.style.backgroundImage =
      `url("https://${window.SERVER_DATA.REACT_APP_BUCKET_NAME}.` +
      `${window.SERVER_DATA.REACT_APP_BUCKET_PATH}/static/` +
      `${window.SERVER_DATA.REACT_APP_TENANT_NAME}/en/assets/images/header_bg.svg")`;
  }, [isResultsPage]);

  useEffect(() => {
    console.log("apparently, SF data changed");
    const clonedSfData = cloneEssentialSearchData(searchData);
    if (isResultsPage && !isEqualCustomized(clonedSfData, prevSFData.current)) {
      setShowSearchButton(true);
      prevSFData.current = clonedSfData;
    }
  }, [isResultsPage, searchData]);

  // The dropdown's `top` used to be a handful of hardcoded rem guesses (see .flexbox/.hotels/
  // .results in SearchForm.module.css) meant to approximate where the clicked field sits below
  // the heading/tab/trip-type rows — wrong whenever a row is hidden (e.g. hotels' hideTripTypes),
  // leaving a large gap and the dropdown floating over the content below. This measures the
  // actual clicked field's position instead, so the dropdown always opens right under it.
  const getFieldNode = useCallback((selector) => {
    if (["dest-location", "src-location"].includes(selector)) {
      return inputRef.current[selector.split("-")[0]];
    } else if (["start-date", "end-date"].includes(selector)) {
      return inputRef.current[selector];
    } else if (selector === "passengers") {
      return inputRef.current.passengers;
    }
    return null;
  }, []);

  const [dropdownTop, setDropdownTop] = useState(null);

  const updateDropdownTop = useCallback(
    (selector) => {
      const node = getFieldNode(selector);
      const anchor = node?.closest(".padded-view");
      if (!node || !anchor) return;
      const fieldRect = node.getBoundingClientRect();
      const anchorRect = anchor.getBoundingClientRect();
      setDropdownTop(fieldRect.bottom - anchorRect.top + 6); // 6px gap under the field
    },
    [getFieldNode],
  );

  const onOpenSelectionElement = useCallback(
    (inputId, tripId, focus = true) => {
      console.log("this is " + inputId);
      const Content = getSearchFormElementContentById(inputId);
      updateDropdownTop(inputId);
      dispatchDropdown({
        type: "OPEN_DROPDOWN",
        selector: inputId,
        tripId: tripId,
        focus,
        content: <Content />,
      });
    },
    [updateDropdownTop],
  );

  // Keeps the dropdown glued to its field while the page scrolls or the window resizes, instead
  // of staying at the position it was measured at when it first opened.
  useEffect(() => {
    if (!dropdownState.currentElement) return;
    const recalc = () => updateDropdownTop(dropdownState.dropdownSelector);
    window.addEventListener("scroll", recalc, true);
    window.addEventListener("resize", recalc);
    return () => {
      window.removeEventListener("scroll", recalc, true);
      window.removeEventListener("resize", recalc);
    };
  }, [dropdownState.currentElement, dropdownState.dropdownSelector, updateDropdownTop]);

  const onCloseSelectionDropdown = useCallback(() => {
    console.log("closing a dropdown");
    dispatchDropdown({ type: "CLOSE_DROPDOWN" });
  }, []);

  const onCheckboxChangeHandler = (id, isChecked) => {
    if (id === "include-accommodation") {
      setCheckAccommodation(isChecked);
    } else if (id === "direct-flights-check") {
      setCheckDirectFlights(isChecked);
    }
  };

  const onClickSearchHandler = () => {
    const res = onSearchStart(
      tripType,
      searchData,
      onOpenSelectionElement,
      checkDirectFlights,
      checkAccommodation,
    );
    if (res) {
      setShowSearchButton(!isResultsPage);
    }
  };

  let cardOpts = {};
  if (dropdownState.dropdownSelector === "passengers") {
    cardOpts = { padding: "1.55rem 1.25rem 1.25rem" };
  } else if (
    dropdownState.dropdownSelector === "dest-location" ||
    dropdownState.dropdownSelector === "src-location"
  ) {
    cardOpts = { padding: "1.875rem 1.25rem 1.25rem" };
  } else if (
    dropdownState.dropdownSelector === "start-date" ||
    dropdownState.dropdownSelector === "end-date"
  ) {
    cardOpts = { padding: "1.38rem 1.12rem 1.875rem", overflowY: "hidden" };
  }

  useEffect(() => {
    console.log(dropdownState.dropdownSelector);
    const clickAwayHandler = (e) => {
      let r, isPassengers;
      if (["dest-location", "src-location"].includes(dropdownState.dropdownSelector)) {
        r = inputRef.current[dropdownState.dropdownSelector.split("-")[0]];
      } else if (["start-date", "end-date"].includes(dropdownState.dropdownSelector)) {
        r = inputRef.current[dropdownState.dropdownSelector];
      } else if (dropdownState.dropdownSelector === "passengers") {
        r = inputRef.current.passengers;
        isPassengers = true;
      }

      if (
        elementRef.current &&
        r &&
        !elementRef.current.contains(e.target) &&
        !r.contains(e.target)
      ) {
        onCloseSelectionDropdown();
        if (isPassengers) {
          dispatch(searchDataActions.fillUserInputPassengerOptions());
        }
      }
    };

    const keyUpHandler = (e) => {
      if (e.key === "Escape" && dropdownState.currentElement) {
        onCloseSelectionDropdown();
      }
    };

    const keyDownHandler = (e) => {
      if (e.key === "Tab" && e.shiftKey && dropdownState.currentElement) {
        if (
          document.activeElement === elementRef.current ||
          !elementRef.current.contains(document.activeElement)
        ) {
          onCloseSelectionDropdown();
        }
      }
    };

    const timerId = setTimeout(() => {
      document.addEventListener("mousedown", clickAwayHandler);
      document.addEventListener("keyup", keyUpHandler);
      document.addEventListener("keydown", keyDownHandler);
    }, 1);

    return () => {
      clearTimeout(timerId);
      document.removeEventListener("mousedown", clickAwayHandler);
      document.removeEventListener("keyup", keyUpHandler);
      document.removeEventListener("keydown", keyDownHandler);
    };
  }, [dispatch, elementRef, inputRef, dropdownState, onCloseSelectionDropdown]);

  const sfProps = {
    ref: inputRef.current,
    isButtonEnabled: showSearchButton,
    onSearchFormClick: onOpenSelectionElement,
    onStartSearch: onClickSearchHandler,
    onCheckboxChange: onCheckboxChangeHandler,
    checkValueAccom: checkAccommodation,
    isResultsPage: isResultsPage,
    hideCheckboxes,
    searchType,
  };

  return (
    <PaddedView>
      {!isResultsPage && (
        <div className={classes.titleHeader}>
          <h1>{heading}</h1>
          <SearchTypeSwitch
            current={searchType}
            onSelect={onSelectSearchType}
            types={[
              { name: "charters", icon: IconBeach, label: getStringRes("main.nav.charters") },
              { name: "flights", icon: IconPlane, label: getStringRes("main.nav.airtickets") },
              { name: "tours", icon: IconBus, label: getStringRes("main.nav.tours") },
              {
                name: "hotels",
                icon: IconBuildingSkyscraper,
                label: getStringRes("main.nav.hotels"),
              },
            ]}
          />
        </div>
      )}
      {!hideTripTypes && (
        <TravelTypeItemGroup onSelected={onSelectTripTypeHandler} tripType={tripType} />
      )}
      <ModalElementContext.Provider
        value={{
          onClose: onCloseSelectionDropdown,
          targetTripId: dropdownState.targetTripId,
          hideCabinOptions,
        }}>
        {dropdownState.currentElement && (
          <SFDropdownElement
            searchType={searchType}
            isResultsPage={isResultsPage}
            isButtonEnabled={showSearchButton}
            ref={elementRef}
            opts={cardOpts}
            focusOnInit={dropdownState.focusOnInit}
            isOneway={tripType === TRIP.oneway}
            isClosing={dropdownState.isElementClosing}
            elementId={dropdownState.dropdownSelector}
            measuredTop={dropdownTop}>
            {dropdownState.currentElement}
          </SFDropdownElement>
        )}
        <DesktopSingleSearchForm {...sfProps} checkValueDirect={checkDirectFlights} />
      </ModalElementContext.Provider>
    </PaddedView>
  );
}

const SFDropdownElement = forwardRef(function SFDropdownElement(
  {
    opts,
    isClosing,
    focusOnInit,
    elementId,
    children,
    isResultsPage,
    isOneway,
    isButtonEnabled,
    searchType,
    measuredTop,
  },
  ref,
) {
  const { targetTripId } = useContext(ModalElementContext);
  const orderNum = useSelector((state) =>
    state.search.trips.findIndex((trip) => trip.id === targetTripId),
  );
  const maxOrder = useSelector((state) => state.search.trips.length);
  const dropdownClass =
    elementId === "passengers" ? classes["row" + (maxOrder - 1)] : classes["row" + orderNum];

  useEffect(() => {
    if (focusOnInit && ref?.current) {
      ref.current.tabIndex = 0;
      ref.current.focus();
    }

    return () => {
      if (elementId) {
        document.getElementById(elementId)?.focus();
      }
    };
  }, [elementId, focusOnInit, ref]);

  return (
    <div
      className={`${classes.flexbox} ${isResultsPage ? classes.results : ""} ${
        isOneway ? classes.oneway : ""
      } ${isButtonEnabled ? "" : classes.noSFButton} ${classes[searchType]}`}
      // Measured from the actual clicked field (see updateDropdownTop in DesktopSearchForm) —
      // overrides the CSS file's hardcoded rem guesses, which is only a fallback now for the
      // brief instant before the first measurement lands.
      style={measuredTop != null ? { top: `${measuredTop}px` } : undefined}>
      <div className={classes.dropdownGrid}>
        <Card
          key={elementId + orderNum}
          opts={opts}
          ref={ref}
          className={`${classes.dropdown} ${dropdownClass} ${
            elementId ? classes[elementId] : ""
          }`}
          shadowLg
          {...(isResultsPage ? { cornerXSm: true } : { cornerSm: true })}>
          {children}
        </Card>
      </div>
    </div>
  );
});
